import { useNavigate } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { useApiQuery, useApiMutation } from "@/lib/api/hooks"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

export function SubjectsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: subjects, isPending } = useApiQuery("get", "/subjects")
  const addMutation = useApiMutation("post", "/user/subjects")

  const hasAny = (subjects ?? []).some((s) => s.selected)

  async function handlePick(subjectId: string) {
    await addMutation.mutateAsync({ body: { subjectId } })
    await queryClient.invalidateQueries({ queryKey: ["get", "/dashboard/subjects"] })
    await queryClient.invalidateQueries({ queryKey: ["get", "/subjects"] })
    navigate("/")
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border px-6 py-4 flex items-center gap-4">
        {hasAny && (
          <button
            onClick={() => navigate("/")}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Back
          </button>
        )}
        <h1 className="text-lg font-semibold">
          {hasAny ? "Add a subject" : "Pick a subject to start learning"}
        </h1>
      </header>

      <main className="px-6 py-8 max-w-5xl mx-auto">
        {isPending ? (
          <div className="flex items-center justify-center py-24">
            <span className="text-sm text-muted-foreground">Loading...</span>
          </div>
        ) : !subjects?.length ? (
          <div className="flex items-center justify-center py-24">
            <p className="text-sm text-muted-foreground">No subjects available yet.</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {subjects.map((subject) => {
              const disabled = subject.selected || addMutation.isPending
              return (
                <button
                  key={subject.id}
                  disabled={disabled}
                  onClick={() => !subject.selected && handlePick(subject.id)}
                  className={cn(
                    "text-left rounded-lg border p-7 transition",
                    subject.selected
                      ? "opacity-60 cursor-default border-border"
                      : "hover:border-primary/50 cursor-pointer"
                  )}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0 space-y-2">
                      <h3 className="font-semibold text-lg leading-tight">{subject.name}</h3>
                      {subject.description && (
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {subject.description}
                        </p>
                      )}
                    </div>
                    {subject.selected && (
                      <span className="shrink-0 text-xs font-medium rounded-full bg-muted px-2 py-1 text-muted-foreground">
                        Selected
                      </span>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        )}

        {hasAny && (
          <div className="mt-8 flex justify-center">
            <Button variant="outline" onClick={() => navigate("/")}>
              Done
            </Button>
          </div>
        )}
      </main>
    </div>
  )
}
